import { FC, MouseEvent, ReactNode, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FormItem, ValidationState } from '../Form/FormItem';
import { IconButton } from '../IconButton/IconButton';

const ExpandButton = styled.div`
  position: absolute;
  right: 0px;
  z-index: 1;
  padding: 10px;
`;

export interface IProps {
  /** textarea ARIA label tag */
  ariaLabel: string;
  /** textarea label */
  label?: string;
  /** Will be passed through to the labels for attribute */
  textareaId?: string;
  /** textarea type */
  type?: string;
  /** Specifies whether the textarea is required  */
  required?: boolean;
  /** Specifies whether the textarea is disabled  */
  disabled?: boolean;
  /** textarea placeholder */
  placeholder?: string;
  /** textarea value */
  value?: string | number;
  /** textarea name */
  name?: string;
  /** textarea tabindex */
  tabIndex?: number;
  /** Automatically focus when page loads */
  autoFocus?: boolean;
  /** Invoke onChange of textarea  */
  onChange?: (value?: string) => void;
  /** textarea validation state (complete, warning)  */
  validationState?: ValidationState;
  /** textarea validation message  */
  validationMessage?: string;
  /** Adds spacing below the component (default: true) */
  spacingBelow?: boolean;
  /** A hint can be passed as HTML to explain what the textarea is used for */
  hint?: any;
  /** Optional tooltip heading. Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description. Defaults to undefined. */
  tooltipDescription?: string;
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  tooltipComponent?: ReactNode;
  /** Optional set height for the text area. Default to a minimum height of 80px */
  height?: number;
  /** Optional open button which can be configured by the user. */
  onOpenButtonClicked?: (e?: MouseEvent) => void;
  /** Set textarea to fullscreen. Defaults to false. Default to a minimum height of 80px */
  fullscreen?: boolean;
  /** Allows the textarea to span the full width of its container */
  fullWidth?: boolean;
  /** When true, the textarea can be resized vertically by the user. */
  verticalResize?: boolean;
  /** Grows with content; `height` is the minimum height when set. */
  autoGrow?: boolean;
}

const textareaNonDomProps = new Set([
  'ariaLabel',
  'textareaId',
  'fullscreen',
  'height',
  'validationState',
  'validationMessage',
  'spacingBelow',
  'hint',
  'label',
  'tooltipHeading',
  'tooltipDescription',
  'tooltipWidth',
  'tooltipComponent',
  'fullWidth',
  'onOpenButtonClicked',
]);

const TextareaElement = styled.textarea.withConfig({
  shouldForwardProp: prop => typeof prop === 'string' && !prop.startsWith('$') && !textareaNonDomProps.has(prop),
})<IProps & { $verticalResize?: boolean; $autoGrow?: boolean }>`
  ${props => props.theme.partials.newForm.item.default};
  color: ${props => props.theme.vars.newColor.darkGS1};
  min-height: ${({ height, $verticalResize, $autoGrow, fullscreen }) => {
    if (fullscreen) return '80px';
    if (($autoGrow || $verticalResize) && height != null && height > 0) return `${height}px`;
    return '80px';
  }};
  height: ${({ fullscreen, height, $verticalResize, $autoGrow }) => {
    if (fullscreen) return '100%';
    if ($autoGrow || $verticalResize) return 'auto';
    if (height) return `${height}px`;
    return 'auto';
  }};
  overflow: ${({ $autoGrow }) => ($autoGrow ? 'hidden' : 'auto')};
  padding: 15px;
  padding-right: ${props => (props.onOpenButtonClicked ? '60px' : '15px')};
  background-color: ${props => (props.disabled ? props.theme.vars.newColor.lightGS2 : props.theme.vars.newColor.white)};
  resize: ${({ $verticalResize }) => ($verticalResize ? 'vertical' : 'none')};

  &::placeholder {
    color: ${props => props.theme.vars.newColor.lightGS1};
  }
  &:disabled,
  &:disabled:hover {
    cursor: not-allowed;
  }
`;

export const Textarea: FC<IProps> = props => {
  const { verticalResize, autoGrow, ...restProps } = props;
  const {
    // Props
    ariaLabel,
    label,
    type,
    required,
    disabled,
    placeholder,
    value,
    tabIndex,
    autoFocus,
    validationState,
    validationMessage,
    spacingBelow,
    hint,
    tooltipHeading,
    tooltipDescription,
    tooltipWidth,
    tooltipComponent,
    textareaId,
    onOpenButtonClicked,
    height,
    onChange,
    fullscreen,
    fullWidth,
    name,
  } = restProps;

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!autoGrow || fullscreen) return;
    const el = textareaRef.current;
    if (!el) return;

    const applyOverflowY = () => {
      requestAnimationFrame(() => {
        el.style.overflowY = el.scrollHeight > el.clientHeight + 1 ? 'auto' : 'hidden';
      });
    };

    const syncHeight = () => {
      const minPx = height != null && height > 0 ? height : 80;
      el.style.height = 'auto';
      el.style.height = `${Math.max(el.scrollHeight, minPx) + 3}px`;
      applyOverflowY();
    };

    syncHeight();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const ro = new ResizeObserver(() => applyOverflowY());
    ro.observe(el);
    return () => ro.disconnect();
  }, [autoGrow, fullscreen, height, value]);

  const handleChange = e => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  return (
    <FormItem
      label={label}
      validationMessage={validationMessage}
      validationState={validationState}
      spacingBelow={spacingBelow}
      hint={hint}
      tooltipHeading={tooltipHeading}
      tooltipDescription={tooltipDescription}
      tooltipWidth={tooltipWidth}
      tooltipComponent={tooltipComponent}
      disabled={disabled}
      for={textareaId}
      fullscreen={fullscreen}
      fullWidth={fullWidth}
    >
      {onOpenButtonClicked ? (
        <ExpandButton>
          <IconButton ariaLabel="expand" icon="expand-arrows-alt" onClick={onOpenButtonClicked} />
        </ExpandButton>
      ) : null}
      <TextareaElement
        {...restProps}
        ref={autoGrow && !fullscreen ? textareaRef : undefined}
        $verticalResize={verticalResize === true}
        $autoGrow={autoGrow === true}
        type={type}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        onChange={handleChange}
        value={value}
        name={name}
        tabIndex={tabIndex}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        autoComplete={type === 'email' ? 'email' : 'off'}
        id={textareaId}
        validationState={validationState}
        height={height}
        data-testid="textarea"
        fullscreen={fullscreen}
      />
    </FormItem>
  );
};
